/**
 * AnswerEvent — renderiza tipo 'answer'
 * payload: { markdown, attachments?[], sources?[] }
 * R-PT-09: no almacena estado; rehidrata desde el chat store.
 */

import { useRef, useEffect } from 'react'
import { enlazarCitas } from '../../lib/citaIku.js'
import { separarAccion } from '../../lib/bloqueAccion.js'

// Markdown mínimo sin dependencia externa (bold, italic, headers, code y citas de iKu de Saber, GEN-EKB-023).
// Solo se enlazan las citas `[Título](#/saber/iku/<id>)`: cualquier otro enlace queda como texto.
// La dependencia react-markdown se usará cuando se instalen los paquetes.
// Por ahora: renderizador inline ligero.
function SimpleMarkdown({ text }) {
  // Escapar HTML y aplicar reglas básicas
  const escapado = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
  // Citas de iKu (GEN-EKB-023): se enlazan antes que el resto, con el título ya escapado
  const html = enlazarCitas(escapado)
    // Headers
    .replace(/^### (.+)$/gm, '<h3>$1</h3>')
    .replace(/^## (.+)$/gm, '<h2>$1</h2>')
    .replace(/^# (.+)$/gm, '<h1>$1</h1>')
    // Bold / italic
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    // Inline code
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    // Block quote
    .replace(/^&gt; (.+)$/gm, '<blockquote>$1</blockquote>')
    // Unordered list items
    .replace(/^[-*] (.+)$/gm, '<li>$1</li>')
    // Paragraphs (doble salto)
    .split(/\n\n+/)
    .map(block => {
      if (block.startsWith('<h') || block.startsWith('<blockquote') || block.startsWith('<li'))
        return block
      return `<p>${block.replace(/\n/g, '<br>')}</p>`
    })
    .join('\n')

  return (
    <div
      className="markdown-body"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

// «Resumen: …» → etiqueta en negrita y el valor; una línea sin etiqueta, tal cual. Solo texto: nada de lo que llegue se interpreta como HTML.
const RE_ETIQUETA = /^([^:]{1,24}):\s+(.+)$/

function DetalleAccion({ linea }) {
  const m = RE_ETIQUETA.exec(linea)
  return <p>{m ? <><strong>{m[1]}:</strong> {m[2]}</> : linea}</p>
}

export default function AnswerEvent({ payload, onReply }) {
  const { markdown = '', sources = [] } = payload
  // GEN-EKB-027: Jimmy cierra con un bloque de acción (confirmar el KB de destino); cada opción responde por la persona.
  const { texto, accion } = separarAccion(markdown)

  return (
    <div className="event-answer">
      <SimpleMarkdown text={texto} />
      {accion && (
        <div className="accion" role="group" aria-label={accion.titulo}>
          <div className="accion__tipo">{accion.tipo ?? 'Confirmar'}</div>
          <h4 className="accion__titulo">{accion.titulo}</h4>
          {accion.detalle && (
            <div className="accion__detalle">
              {accion.detalle.map((linea, i) => <DetalleAccion key={i} linea={linea} />)}
            </div>
          )}
          <div className="accion__btns">
            {accion.opciones.map((o, i) => (
              <button key={o} type="button" className={`btn${i === 0 ? ' btn--primario' : ''}`} onClick={() => onReply?.(o)}>{o}</button>
            ))}
          </div>
        </div>
      )}
      {sources.length > 0 && (
        <div className="event-answer__sources">
          <span className="event-answer__sources-label">Fuentes:</span>
          {sources.map((s, i) => (
            <span key={i} className="event-answer__source">{s.label}</span>
          ))}
        </div>
      )}
    </div>
  )
}
